import type { LevelsCompletionFilter } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._components.toolbar";
import {
  LEVELS_LIST_TOP_GAP,
  LevelsList,
  LevelsPackDescriptionHeader,
  LevelsPremiumUpgradePrompt,
  getLevelsListEntryLabel,
  getLevelsPackDescription,
  isIndexedCharacterLevel,
  isIndexedPronunciationLevel,
  type IndexedLevel,
  type LevelsListEntry,
} from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._components.levels-list";
import { getWordLevels } from "@-/dev.improve.haniverse.assets.words.levels";
import {
  getCurriculumMaterialTabValue,
  withoutHardcodedPronunciationLevelWords,
  levelMatchesCurriculumMaterialTab,
  type WordLevel,
} from "@-/dev.improve.haniverse.lib.level";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { BrowseLevelsLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._context";
import {
  useBrowseLevelsPageNavigator,
  useBrowseLevelsStudyGraphemesQuery,
} from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels.navigator";
import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { ScreenReadyMarker } from "@-/dev.improve.haniverse.components.ui.screen-ready-marker";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useIsPremium } from "@-/dev.improve.haniverse.components.superwall";
import { useIsNavigationTreeFocused } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs._lib.focus";
import { useBrowseTabsContentBottomInset } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs._lib.content-bottom-inset";
import {
  RouteBodyStateProvider,
  useHiddenBodyRetention,
} from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { View } from "@-/com.npmjs.react-native";
import type { LegendListRef } from "@-/com.npmjs.legendapp__list";
import { Profiler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGlassHeaderInset } from "@-/dev.improve.haniverse.components.ui.header-scroll";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import { useLevelsPreviewAutoScroll } from "#use-levels-preview-auto-scroll";

// TEMP DIAGNOSTIC (perf investigation): count React commits of the levels-list subtree per phase and
// dump them ~5s after the first commit. `update` counts are the churn we're chasing.
const __cc: Record<string, number> = {};
let __ccSched = false;
function __onListRender(id: string, phase: string): void {
  const k = `${id}:${phase}`;
  __cc[k] = (__cc[k] ?? 0) + 1;
  if (!__ccSched) {
    __ccSched = true;
    setTimeout(() => {
      // eslint-disable-next-line no-console -- TEMP perf diagnostic
      console.log(`[list-commits] ${JSON.stringify(__cc)}`);
      for (const key of Object.keys(__cc)) {
        delete __cc[key];
      }
      __ccSched = false;
    }, 5000);
  }
}

/**
 * What the list treats as "studied" before the study-data query has settled: nothing. The catalog is
 * static and renders progressively ahead of completion data (see `FocusedLevelsListView`), so until
 * then no level counts as complete. Completion-dependent chrome (the toolbar's return /
 * completion-filter controls, the screen-ready marker) waits on the query's own pending state.
 */
const NO_STUDY_DATA_YET: ReadonlySet<string> = new Set();

/** Returns true when every (non-empty) character in the level has been added to study. */
function isLevelCompleted(level: IndexedLevel["level"], addedCharacterSet: ReadonlySet<string>) {
  if (hasLength(level.characters, 0)) {
    return false;
  }
  return level.characters.every((character) => addedCharacterSet.has(character));
}

export interface LevelsListViewProps {
  /** Fixed completion filter for this sub-route (`all` / `incomplete` / `completed`). */
  completionFilter: LevelsCompletionFilter;
}

function BrowseLevelsScreenReadyMarker({ graphemeCount }: { graphemeCount: number }) {
  // This child only mounts once the study data is ready. Snapshot the count at that moment because
  // ObserveInteractiveMarker records params once on mount; later database syncs can update the
  // studied set while the marker remains mounted.
  const [params] = useState(() => ({ graphemeCount }));
  return <ScreenReadyMarker params={params} screen="browse" />;
}

/**
 * The levels list for a single completion sub-route. It filters the shared levels by the route's
 * fixed `completionFilter`, tracks the user's current level, and — while focused — handles the
 * current-level scroll request from a Browse tab re-tap. The view mode is read from the context so it
 * persists while switching between completion filters.
 *
 * The layout's keep-alive navigator preserves each completion sub-route's route shell, but this
 * component mounts its expensive catalog child only while the route's full ancestor tree is focused.
 * Only that focused child may handle the shared current-level scroll request.
 */
export function LevelsListView(props: LevelsListViewProps) {
  // Mounted for as long as this is the route Browse is SHOWING, and for a grace period while
  // another bottom tab is in front. Unmounting on every bottom-tab blur made each return to Browse
  // rebuild the whole catalog (1,800+ component renders and their native views in one commit — the
  // slowest tab switch in the app), so a quick round trip keeps it. A longer absence (or an iOS
  // memory warning) releases it: a hidden catalog costs only memory, but a lot of it, and the list
  // below gates its SQL and change-event work on full-tree focus either way.
  const isNavigationTreeFocused = useIsNavigationTreeFocused("bottom-tab");
  const isVisibleToLearner = useIsNavigationTreeFocused();
  const isRetainedWhileHidden = useHiddenBodyRetention(isVisibleToLearner);

  // The completion-filter navigator preserves every visited route so it can restore the selected
  // route without rebuilding its navigation state. Keeping each route's catalog subtree mounted as
  // well is much more expensive: every visited filter retains a LegendList, its native glyph views,
  // and several whole-catalog query observers. The bottom-tab freeze boundary compounds that cost by
  // retaining the active catalog after Cards is blurred.
  //
  // Keep only this tiny focus observer mounted while a SIBLING completion route is showing. Query
  // results remain in TanStack's finite cache for a fast return, while the large derived maps,
  // React fibers, and native views become collectible immediately.
  //
  // The same release happens while a morpheme page is pushed over Browse. The route-body snapshot
  // sits above that boundary so the list's scroll anchor (a row key and an offset) survives it, and
  // the rebuilt catalog reopens where the learner left it instead of jumping to the current level.
  return (
    <RouteBodyStateProvider>
      {isNavigationTreeFocused && isRetainedWhileHidden ? (
        <FocusedLevelsListView {...props} isVisibleToLearner={isVisibleToLearner} />
      ) : null}
    </RouteBodyStateProvider>
  );
}

function FocusedLevelsListView({
  completionFilter,
  isVisibleToLearner,
}: LevelsListViewProps & {
  /** False while another bottom tab is in front: stay mounted, but do and publish nothing. */
  isVisibleToLearner: boolean;
}) {
  const browseTabsContentBottomInset = useBrowseTabsContentBottomInset({ trailingGap: 4 });
  const [levels, hanziScript, isBrowsingStudiedCurriculum, hasPackSheet] = useContextSelectors(
    BrowseLevelsLayoutContext,
    (c) => c.characterLevels,
    (c) => c.browsedHanziScript,
    (c) => c.isBrowsingStudiedCurriculum,
    (c) => c.hasPackSheet,
  );
  const isPremium = useIsPremium();
  const isComingSoon = hanziScript === "traditional";
  const [
    curriculumDataset,
    curriculumGrade,
    setCurriculumGrade,
    gradeParts,
    viewMode,
    shouldScrollToCurrentLevel,
    setShouldScrollToCurrentLevel,
    levelScrollRequest,
    setLevelScrollRequest,
  ] = useContextSelectors(
    BrowseLevelsLayoutContext,
    (c) => c.curriculumDataset,
    (c) => c.curriculumGrade,
    (c) => c.setCurriculumGrade,
    (c) => c.gradeParts,
    (c) => c.viewMode,
    (c) => c.shouldScrollToCurrentLevel,
    (c) => c.setShouldScrollToCurrentLevel,
    (c) => c.levelScrollRequest,
    (c) => c.setLevelScrollRequest,
  );
  const showAllLevels = useBrowseLevelsPageNavigator().replace.mutate;
  const isPremiumPackLocked = !isComingSoon && !isPremium && curriculumGrade > 1;
  const materialTabFilter = useMemo(
    () => ({ dataset: curriculumDataset, gradeParts }),
    [curriculumDataset, gradeParts],
  );
  const matchesSelectedMaterialTab = (level: { curriculumGrade?: number; index: number }) =>
    levelMatchesCurriculumMaterialTab(level, curriculumGrade, materialTabFilter);
  // Progressive: the list renders the static catalog while this settles (see `NO_STUDY_DATA_YET`).
  const studyGraphemesQuery = useBrowseLevelsStudyGraphemesQuery();
  const addedCharacterSet = studyGraphemesQuery.data ?? NO_STUDY_DATA_YET;
  const listRef = useRef<LegendListRef>(null);
  // The route shell stays mounted under the layout's keep-alive navigator. The expensive child is
  // focus-bounded above, and this leaf focus check still gates every shared-toolbar publication.
  // `useIsFocused()` alone keeps reporting true while the preserved Browse tab is behind another
  // bottom tab, so every gate below also requires the learner to actually be looking at it.
  const isRouteFocused = useIsFocused();
  const isFocused = isRouteFocused && isVisibleToLearner;
  const stopPreviewOnManualScroll = useLevelsPreviewAutoScroll({
    enabled: isComingSoon && isFocused,
    listRef,
    scope: `${hanziScript}/${curriculumGrade}`,
  });

  const wordLevelsByCharacterLevel = useMemo(() => {
    const byCharacterLevel = new Map<number, WordLevel[]>();
    // Authored P-level words stay on that surface; only generated batches get word-level rows.
    const catalog =
      hanziScript === "simplified"
        ? withoutHardcodedPronunciationLevelWords(getWordLevels(hanziScript))
        : getWordLevels(hanziScript);
    for (const wordLevel of catalog) {
      const wordLevels = byCharacterLevel.get(wordLevel.characterLevelIndex) ?? [];
      wordLevels.push(wordLevel);
      byCharacterLevel.set(wordLevel.characterLevelIndex, wordLevels);
    }
    return byCharacterLevel;
  }, [hanziScript]);
  // The whole pack, whatever is locked, completed or filtered out: the header describes the pack.
  const packDescription = useMemo(
    () =>
      getLevelsPackDescription({
        dataset: curriculumDataset,
        gradeParts,
        levels,
        tabValue: curriculumGrade,
      }),
    [curriculumDataset, curriculumGrade, gradeParts, levels],
  );

  // The user's current level is the first level (in canonical order) that has characters and isn't
  // fully completed. Its original `index` is its stable identity across filtering.
  // Only the studied curriculum has one: the other script's levels are a read-only preview.
  const currentLevelOriginalIndex = useMemo(() => {
    if (isComingSoon || !isBrowsingStudiedCurriculum) {
      return null;
    }
    for (const [index, level] of levels.entries()) {
      if (!isPremium && (level.curriculumGrade ?? 1) > 1) {
        continue;
      }
      if (!isLevelCompleted(level, addedCharacterSet)) {
        return index;
      }
    }
    return null;
  }, [levels, isComingSoon, isBrowsingStudiedCurriculum, isPremium, addedCharacterSet]);

  const completionFiltered = useMemo(() => {
    const indexed: IndexedLevel[] = levels
      .map((level, index) => ({
        index,
        isLocked: isComingSoon || (!isPremium && (level.curriculumGrade ?? 1) > 1),
        level,
      }))
      .filter(({ level }) => matchesSelectedMaterialTab(level));

    // NOTE: do NOT short-circuit on `addedCharacterSet.size === 0`. With nothing studied, no level
    // is complete, so the "Completed" filter must be empty (not "all"). `isLevelCompleted` already
    // returns `false` for every level in that case, so the real filter is correct for both an empty
    // and a populated study set (and `.every()` short-circuits, so it stays cheap).
    return completionFilter === "all"
      ? indexed
      : indexed.filter(({ level }) => {
          const completed = isLevelCompleted(level, addedCharacterSet);
          return completionFilter === "completed" ? completed : !completed;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- listed via materialTabFilter / grade
  }, [
    levels,
    isComingSoon,
    isPremium,
    curriculumGrade,
    materialTabFilter,
    completionFilter,
    addedCharacterSet,
  ]);

  // Characters and words are one curriculum: each shown character level is followed by its
  // pronunciation level (`2-P1`, the readings of the meaning-only levels before it) and the word
  // levels it completes (`11-W14`, …), all sharing its premium lock.
  const filteredLevels = useMemo((): LevelsListEntry[] => {
    let pronunciationIndex = 0;
    return completionFiltered.flatMap((entry): LevelsListEntry[] => {
      const pronunciationLevel = levels[entry.index];
      const pronunciationEntry =
        pronunciationLevel?.pronunciationCharacters === undefined
          ? []
          : [
              {
                isLocked: entry.isLocked,
                pronunciationIndex: pronunciationIndex++,
                pronunciationLevel,
              },
            ];
      return [
        entry,
        ...pronunciationEntry,
        ...(wordLevelsByCharacterLevel.get(entry.index) ?? []).map((wordLevel) => ({
          isLocked: entry.isLocked,
          wordLevel,
        })),
      ];
    });
  }, [completionFiltered, levels, wordLevelsByCharacterLevel]);

  // Position of the current level within the (filtered) list actually rendered. `null` when the
  // current level isn't present (e.g. on the "Completed" route).
  const currentLevelListIndex = useMemo(() => {
    if (currentLevelOriginalIndex === null) {
      return null;
    }
    const position = filteredLevels.findIndex(
      (entry) => isIndexedCharacterLevel(entry) && entry.index === currentLevelOriginalIndex,
    );
    return position === -1 ? null : position;
  }, [currentLevelOriginalIndex, filteredLevels]);

  const lastAccessibleIndex = filteredLevels.findLastIndex((item) => !item.isLocked);
  const lastAccessibleLevel = filteredLevels[lastAccessibleIndex];
  const scrollToLastAccessibleLevel = useCallback(() => {
    if (lastAccessibleIndex === -1) {
      return;
    }
    void listRef.current?.scrollToIndex({
      animated: true,
      index: lastAccessibleIndex,
      viewOffset: -browseTabsContentBottomInset,
      viewPosition: 1,
    });
  }, [browseTabsContentBottomInset, lastAccessibleIndex]);

  const currentLevel =
    currentLevelOriginalIndex === null ? undefined : levels[currentLevelOriginalIndex];
  const currentLevelMaterialTab =
    currentLevel === undefined
      ? null
      : getCurriculumMaterialTabValue(currentLevel, {
          dataset: curriculumDataset,
          gradeParts,
        });
  const canReturnToCurrentGrade =
    currentLevelMaterialTab !== null &&
    currentLevelMaterialTab !== curriculumGrade &&
    completionFilter !== "completed";
  // Beneath glass chrome (iOS 26) the viewport starts under the status bar: a level brought to the
  // top must stop below the bar and the grade tabs, then leave the same 4px gap the list uses at
  // its sides so the active surface isn't flush against the chrome.
  const topViewOffset = (useGlassHeaderInset() ?? 0) + LEVELS_LIST_TOP_GAP;
  const scrollToCurrentLevel = useCallback(() => {
    if (canReturnToCurrentGrade && currentLevelMaterialTab !== null) {
      setCurriculumGrade(currentLevelMaterialTab);
      return;
    }
    if (currentLevelListIndex === null) {
      return;
    }
    // The pack's first level: back to the very top, so its description shows above it.
    if (currentLevelListIndex === 0) {
      void listRef.current?.scrollToOffset({ animated: true, offset: 0 });
      return;
    }
    void listRef.current?.scrollToIndex({
      animated: true,
      index: currentLevelListIndex,
      viewOffset: topViewOffset,
      viewPosition: 0,
    });
  }, [
    canReturnToCurrentGrade,
    currentLevelMaterialTab,
    currentLevelListIndex,
    setCurriculumGrade,
    topViewOffset,
  ]);

  // A focused Browse re-tap survives the root/grade navigation until this list can handle it.
  useEffect(() => {
    if (
      !shouldScrollToCurrentLevel ||
      !isFocused ||
      completionFilter === "completed" ||
      studyGraphemesQuery.isPending
    ) {
      return;
    }
    scrollToCurrentLevel();
    // Changing grades mounts a different list; let that list scroll after its saved anchor restores.
    if (!canReturnToCurrentGrade) {
      setShouldScrollToCurrentLevel(false);
    }
  }, [
    canReturnToCurrentGrade,
    completionFilter,
    isFocused,
    scrollToCurrentLevel,
    setShouldScrollToCurrentLevel,
    shouldScrollToCurrentLevel,
    studyGraphemesQuery.isPending,
  ]);

  // A level asked for from the pack sheet (a pinyin chart sound's) comes to the top of this list.
  // The request already opened the level's pack; a completion route that hides the level hands the
  // request over to the full list.
  useEffect(() => {
    if (
      levelScrollRequest === null ||
      !isFocused ||
      (completionFilter !== "all" && studyGraphemesQuery.isPending)
    ) {
      return;
    }
    const position = filteredLevels.findIndex((entry) =>
      levelScrollRequest.part === "pronunciation"
        ? isIndexedPronunciationLevel(entry) &&
          entry.pronunciationLevel.index === levelScrollRequest.levelIndex
        : isIndexedCharacterLevel(entry) && entry.index === levelScrollRequest.levelIndex,
    );
    if (position === -1) {
      if (completionFilter === "all") {
        setLevelScrollRequest(null);
      } else {
        showAllLevels();
      }
      return;
    }
    void listRef.current?.scrollToIndex({
      animated: true,
      index: position,
      viewOffset: topViewOffset,
      viewPosition: 0,
    });
    setLevelScrollRequest(null);
  }, [
    completionFilter,
    filteredLevels,
    isFocused,
    levelScrollRequest,
    setLevelScrollRequest,
    showAllLevels,
    studyGraphemesQuery.isPending,
    topViewOffset,
  ]);

  // A completion route with nothing in it keeps its completion message. The catalog itself is static
  // and non-empty (completion-dependent controls stay gated on the study query's pending state in
  // the toolbar).
  if (hasLength(filteredLevels, 0)) {
    const message =
      completionFilter === "completed"
        ? "No levels completed in this grade"
        : "No incomplete levels in this grade.";
    return (
      <View className="flex-1 items-center justify-center px-8">
        <Text className="text-center text-muted-foreground">{message}</Text>
      </View>
    );
  }

  return (
    <View className="min-h-0 flex-1">
      {/* Keep the catalog available while the completion-status query settles. In particular, the
          native sign-in sync can keep that query in flight for several seconds; hiding the entire
          list behind it leaves a blank Browse screen and prevents users from opening any level.
          Completion-dependent controls remain disabled while the study query is pending (toolbar).
          A pending query may initially position this list at Level 1, then the return-to-current
          control provides the stable final position as soon as study data is ready. */}
      <Profiler id={`levels-list-${completionFilter}`} onRender={__onListRender}>
        <LevelsList
          contentBottomPadding={browseTabsContentBottomInset}
          currentLevelIndex={currentLevelListIndex}
          initialScrollIndex={
            // At the pack's first level, open at the top so its description shows above it.
            currentLevelListIndex === null || currentLevelListIndex === 0
              ? undefined
              : {
                  index: currentLevelListIndex,
                  viewOffset: topViewOffset,
                  viewPosition: 0,
                }
          }
          // Both scripts' curricula open at grade 1: the script keeps their lists apart.
          key={`${hanziScript}/${curriculumGrade}`}
          levelsList={filteredLevels}
          ListHeaderComponent={
            // The pack sheet opens with the description; only a preview without one lists it.
            packDescription === null || isPremiumPackLocked || hasPackSheet ? null : (
              <View className="mb-1">
                <LevelsPackDescriptionHeader {...packDescription} />
              </View>
            )
          }
          listRef={listRef}
          lockedLevelPrompt={
            isComingSoon ? undefined : (
              <LevelsPremiumUpgradePrompt
                onReturn={scrollToLastAccessibleLevel}
                packDescription={
                  isPremiumPackLocked && !hasPackSheet ? (packDescription ?? undefined) : undefined
                }
                returnLabel={
                  lastAccessibleLevel === undefined
                    ? undefined
                    : `Back to ${getLevelsListEntryLabel(lastAccessibleLevel)}`
                }
              />
            )
          }
          onManualScroll={isComingSoon ? stopPreviewOnManualScroll : undefined}
          // A grade switch (including Browse's return-to-current-grade) opens at the current
          // level; only returning to the same grade restores the learner's scroll position.
          scrollPositionScope={`${hanziScript}/${curriculumGrade}`}
          viewMode={viewMode}
        />
      </Profiler>
      {/* No bottom fade here, deliberately. The sub-tab bar is a real UIKit liquid-glass capsule,
          and the material's whole job is to show the content passing beneath it. Dissolving that
          content into an opaque surface-coloured band first leaves the glass nothing to refract,
          so the bar reads as a flat panel sitting on a blank strip. The scroll viewport instead
          runs to the true bottom edge and the glass overlays it; `browseTabsContentBottomInset`
          keeps the last row scrollable clear of the capsule. */}
      {/* Screen-render timing end mark. Mounts (and fires `markInteractive` once) when the Browse
          Levels page's study data has settled and the grapheme list is on screen — the
          "navigated → rendered" completion Leon wants to measure (Browse taking ~2s to show
          graphemes). Gated to the `all` sub-route so only ONE marker fires per Browse open (only the
          active completion route mounts its expensive view); gated on the study query having settled
          so it marks true readiness, not the empty
          first-render placeholder. */}
      {completionFilter === "all" && !studyGraphemesQuery.isPending ? (
        <BrowseLevelsScreenReadyMarker graphemeCount={addedCharacterSet.size} />
      ) : null}
    </View>
  );
}
